import { inject, Injectable, Injector, isDevMode, runInInjectionContext, signal } from '@angular/core';
import {
  deleteField,
  doc,
  Firestore,
  onSnapshot,
  setDoc,
  Unsubscribe,
  runTransaction,
  Transaction,
} from '@angular/fire/firestore';
import { EventBus, EventBusEnum } from './event-bus.service';
import { AuthService } from './auth.service';
import { ProjectRoles } from '../models/project-roles.model';
import { Router } from '@angular/router';

export interface InviteData {
  invites: {
    [projectId: string]: {
      project_name: string;
      inviter: string;
      role: string;
    };
  };
}

export enum InviteStatus {
  ACCEPTED = 'ACCEPTED',
  DECLINED = 'DECLINED',
  PENDING = 'PENDING',
}

interface InviteStatusReadableMap {
  [key: string]: string;
}

export const InviteStatusReadable: InviteStatusReadableMap = {
  [InviteStatus.ACCEPTED]: 'angenommen',
  [InviteStatus.DECLINED]: 'abgelehnt',
  [InviteStatus.PENDING]: 'ausstehend',
};

@Injectable({
  providedIn: 'root',
})
export class InviteListenerService {
  private injector = inject(Injector);
  private eventBus = inject(EventBus);
  private authService = inject(AuthService);
  private router = inject(Router);

  private db: Firestore = inject(Firestore);

  private unsub: Unsubscribe | undefined;
  private _inviteData: InviteData | undefined;

  public nrInvites = signal(0);

  initialize() {
    this.eventBus.event$.subscribe((event) => {
      if (event.type === EventBusEnum.USER_LOGGED_IN) {
        this.initializeAtLogin(event.payload['tenantId'], event.payload['email']);
      } else if (event.type === EventBusEnum.USER_LOGGED_OUT) {
        this.unsubscribe();
      }
    });
  }

  initializeAtLogin(tenantId: string, email: string) {
    if (this.unsub === undefined) {
      this.unsub = runInInjectionContext(this.injector, () =>
        onSnapshot(doc(this.db, `tenants/${tenantId}/invites`, email), (docSnapshot) => {
          this._inviteData = docSnapshot.data() as InviteData;
          if (this._inviteData != undefined) {
            this.nrInvites.set(Object.keys(this._inviteData?.['invites']).length);
          }
        }),
      );
    }
  }

  unsubscribe() {
    if (this.unsub) {
      this.unsub();
    }
    this.unsub = undefined;
  }

  get inviteData(): InviteData | undefined {
    return this._inviteData;
  }

  async removeInvite(projectId: string) {
    if (await this.tokenExpired()) {
      this.router.navigate([this.authService.tenantAlias, 'login']);
      return;
    }

    this.removeInviteFromPersonalInvitesList(projectId);
    this.flagInviteAsDeclinedAtProject(projectId);
  }

  private removeInviteFromPersonalInvitesList(projectId: string, transaction?: Transaction) {
    runInInjectionContext(this.injector, () => {
      const docRef = doc(
        this.db,
        `tenants/${this.authService.dataSyncUser.tenantId}/invites/${this.authService.dataSyncUser.email}`,
      );
      const data = {
        invites: {
          [projectId]: deleteField(),
        },
      };
      transaction ? transaction.set(docRef, data, { merge: true }) : setDoc(docRef, data, { merge: true });
    });
  }

  private flagInviteAsDeclinedAtProject(projectId: string) {
    const data = {
      invites: {
        [this.authService.dataSyncUser.email!]: {
          status: InviteStatus.DECLINED,
        },
      },
    };

    runInInjectionContext(this.injector, () => setDoc(this.getProjectDocRef(projectId), data, { merge: true }));
  }

  async acceptInvite(projectId: string, role: ProjectRoles, project_name: string) {
    if (await this.tokenExpired()) {
      this.router.navigate([this.authService.tenantAlias, '/login']);
      return;
    }

    try {
      await runTransaction(this.db, async (transaction) => {
        this.removeInviteFromPersonalInvitesList(projectId, transaction);
        this.addUserToProject(projectId, role, transaction);
        this.removeInviteFromProject(projectId, transaction);
        this.authService.addProjectToUserData(projectId, project_name, role, transaction);
      });
      if (isDevMode()) {
        console.log('Transaction successfully committed!');
      }
      await this.authService.grantUserRole(projectId, role);
    } catch (e) {
      console.log('Transaction failed: ', e);
    }
  }

  private removeInviteFromProject(projectId: string, transaction: Transaction) {
    const data = {
      invites: {
        [this.authService.dataSyncUser.email!]: deleteField(),
      },
    };

    transaction.set(this.getProjectDocRef(projectId), data, { merge: true });
  }

  private getProjectDocRef(projectId: string) {
    return runInInjectionContext(this.injector, () =>
      doc(this.db, `tenants/${this.authService.dataSyncUser.tenantId}/projects`, projectId),
    );
  }

  private addUserToProject(projectId: string, role: string, transaction: Transaction) {
    const data = {
      roles: {
        [this.authService.dataSyncUser.uid]: {
          role: role,
          email: this.authService.dataSyncUser.email,
        },
      },
    };

    transaction.set(this.getProjectDocRef(projectId), data, { merge: true });
  }

  private async tokenExpired() {
    try {
      await this.authService.forceTokenRefresh();
      return false;
    } catch {
      return true;
    }
  }
}
